// 잠금 화면 — 초기 상태에 따라 세 가지 서브화면을 보여준다:
//  1) 미초기화 → init 폼 (PW 설정 → 복구코드·QR 노출)
//  2) 초기화됨 + 잠김 → 로그인 폼
//  3) 잠금 해제됨 → 자동으로 대시보드 리다이렉트
//  4) 복구 플로우 → 복구 코드 + 새 PW

import { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, type InitResult, type LoginResult } from '../api/auth';
import { useAuthStatus, AUTH_STATUS_KEY } from '../hooks/useAuthStatus';
import { isApiError, formatApiError } from '../api/client';
import { useToast } from '../components/Toast';
import { Spinner } from '../components/Spinner';
import { CopyButton } from '../components/CopyButton';
import { consumeLogoutReason, type LogoutReason } from '../utils/logoutReason';
import { PasskeyLoginButton } from '../components/PasskeyLoginButton';

type View = 'default' | 'reset' | 'post-init';

export default function Lock() {
  const { data, isLoading, error, refetch, isFetching } = useAuthStatus();
  const qc = useQueryClient();
  const [view, setView] = useState<View>('default');
  const [initResult, setInitResult] = useState<InitResult | null>(null);
  const [newRecoveryCode, setNewRecoveryCode] = useState<string | null>(null);
  // 자동 로그아웃 사유 1회 소비 — 배너 렌더 후 sessionStorage에서 제거됨.
  const [logoutInfo] = useState<{ reason: LogoutReason; at: number } | null>(() => consumeLogoutReason());

  // PWA standalone 모드 감지 — standalone에서 Lock 진입 시 패스키 자동 트리거.
  // window.matchMedia는 SSR 환경에서 undefined일 수 있으므로 안전하게 처리.
  const isPwaStandalone =
    typeof window !== 'undefined' &&
    window.matchMedia('(display-mode: standalone)').matches;

  // stale closure 방지 — watchdog에서 최신 data/error 참조용.
  const dataRef = useRef(data);
  const errorRef = useRef(error);
  useEffect(() => { dataRef.current = data; }, [data]);
  useEffect(() => { errorRef.current = error; }, [error]);

  // 안전망 3 — Lock 마운트 시 auth 상태 강제 invalidate.
  // hard reload 없이 진입한 경우(드문 경로)에도 항상 fresh fetch를 보장한다.
  useEffect(() => {
    void qc.invalidateQueries({ queryKey: AUTH_STATUS_KEY });
  }, [qc]);

  /**
   * watchdog — fetch hang / RQ observer race / Traefik 응답 지연 대응.
   * - 3초 경과 & 데이터 없으면 → 강제 re-invalidate (1차 복구)
   * - 6초 경과 & 데이터 없으면 → window.location.reload() (2차 복구)
   * 정상 환경(50ms 이내 응답)에서는 두 타이머 모두 data 도착 후 취소됨.
   * 운영 stuck 재발 시 DevTools Console에서 [locker.lock] watchdog 로그로 추적.
   */
  useEffect(() => {
    // 이미 data/error 있으면 watchdog 시작 안 함 (정상 흐름)
    if (dataRef.current || errorRef.current) return;

    const t1 = setTimeout(() => {
      if (!dataRef.current && !errorRef.current) {
        console.warn('[locker.lock] watchdog: 3s elapsed, force invalidate');
        void qc.invalidateQueries({ queryKey: AUTH_STATUS_KEY });
      }
    }, 3000);

    const t2 = setTimeout(() => {
      if (!dataRef.current && !errorRef.current) {
        console.warn('[locker.lock] watchdog: 6s elapsed, force reload');
        window.location.reload();
      }
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
    // 마운트 1회만 실행 — qc는 안정적 참조이므로 deps 포함
  }, [qc]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) return <Spinner label="상태 확인 중..." />;
  // 서버 응답 실패(타임아웃/네트워크/5xx) 시 재시도 UI — 무한 spinner 방지.
  if (error) {
    const detail = isApiError(error) ? formatApiError(error, '서버 응답 실패') : '서버 응답 실패';
    return (
      <div className="lock">
        <div className="lock__card">
          <h1 className="lock__title">🔒 Locker</h1>
          <div className="error-inline" style={{ marginBottom: 14 }}>{detail}</div>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void refetch()}
            disabled={isFetching}
            style={{ width: '100%' }}
          >
            {isFetching ? '재시도 중…' : '다시 시도'}
          </button>
        </div>
      </div>
    );
  }
  if (data?.unlocked) return <Navigate to="/" replace />;

  const showPostInit = view === 'post-init' && initResult !== null;
  const showInit = !showPostInit && view === 'default' && data && !data.initialized;
  const showLogin = !showPostInit && view === 'default' && data?.initialized;
  const showReset = !showPostInit && view === 'reset';

  return (
    <div className="lock">
      <div className="lock__card">
        <h1 className="lock__title">🔒 Locker</h1>

        {logoutInfo && showLogin && (
          <div
            className="callout"
            style={{
              marginBottom: 14,
              borderLeft: '4px solid var(--warn)',
              fontSize: '0.9em',
            }}
          >
            {logoutInfo.reason === 'idle' && '30분 이상 활동이 없어 자동 로그아웃되었습니다.'}
            {logoutInfo.reason === 'unauthorized' && '세션이 만료되어 로그아웃되었습니다.'}
            {logoutInfo.reason === 'manual' && '로그아웃되었습니다.'}
          </div>
        )}

        {showPostInit && (
          <PostInit
            result={initResult!}
            onConfirm={() => { setInitResult(null); setView('default'); }}
          />
        )}

        {showInit && (
          <InitForm
            onDone={(r) => { setInitResult(r); setView('post-init'); }}
          />
        )}

        {showLogin && (
          <>
            {/* 패스키 버튼 — WebAuthn 지원 + 등록된 패스키 있을 때만 자동 노출 */}
            <PasskeyLoginButton
              onSuccess={() => void qc.invalidateQueries({ queryKey: AUTH_STATUS_KEY })}
              autoTrigger={isPwaStandalone}
            />
            <LoginForm
              recoveryCodeOnScreen={newRecoveryCode}
              onAckRecoveryCode={() => setNewRecoveryCode(null)}
              myIpIsTrusted={data?.myIpIsTrusted ?? false}
              myIp={data?.myIp ?? null}
            />
            <div className="lock__links">
              <button type="button" className="link" onClick={() => setView('reset')}>복구 코드로 재설정</button>
            </div>
          </>
        )}

        {showReset && (
          <ResetForm
            onDone={(newCode) => {
              setNewRecoveryCode(newCode);
              setView('default');
            }}
            onCancel={() => setView('default')}
          />
        )}
      </div>
    </div>
  );
}

// ─── 하위 폼들 ────────────────────────────────────

function InitForm({ onDone }: { onDone: (r: InitResult) => void }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const toast = useToast();
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (pw: string) => authApi.init(pw),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: AUTH_STATUS_KEY });
      onDone(r);
    },
    onError: (err) => toast.error(formatApiError(err, '초기화 실패')),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('비밀번호는 8자 이상이어야 합니다'); return; }
    if (password !== confirm) { toast.error('확인 비밀번호가 일치하지 않습니다'); return; }
    mut.mutate(password);
  };

  return (
    <form onSubmit={onSubmit} className="stack">
      <p className="muted">최초 설정 — 마스터 패스워드를 정해주세요.</p>
      <label>
        <span>마스터 패스워드 (8자 이상)</span>
        <input type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
      </label>
      <label>
        <span>확인</span>
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </label>
      <button type="submit" className="btn btn--primary" disabled={mut.isPending}>
        {mut.isPending ? '설정 중…' : '설정하기'}
      </button>
    </form>
  );
}

function PostInit({ result, onConfirm }: { result: InitResult; onConfirm: () => void }) {
  // 복구 코드·TOTP 시크릿 보관 확인 체크박스 — 둘 다 체크해야 다음 진행 가능.
  const [savedRecovery, setSavedRecovery] = useState(false);
  const [savedTotp, setSavedTotp] = useState(false);
  const allSaved = savedRecovery && savedTotp;

  return (
    <div className="stack">
      {/* 경고 배너 — 이 화면 이후 복구 코드를 다시 볼 수 없음 강조 */}
      <div className="post-init__critical-warning">
        <span className="post-init__critical-warning__icon">&#9888;</span>
        <div>
          <b>이 코드는 지금 이 화면에서만 볼 수 있습니다.</b><br />
          즉시 종이로 인쇄하거나 물리 금고에 보관하세요.<br />
          <small>복구 코드 분실 시 데이터를 복구할 방법이 없습니다.</small>
        </div>
      </div>

      <div className="post-init__section">
        <div className="post-init__label">
          복구 코드
          <span className="post-init__label-badge">1회용 — 사용 후 즉시 갱신됨</span>
        </div>
        <div className="post-init__code">
          <code>{result.recoveryCode}</code>
          <CopyButton value={result.recoveryCode} />
        </div>
        <p className="post-init__hint">
          종이에 적거나 인쇄 후 물리 금고에 보관하세요. 클라우드(Google Drive 등)에 평문 저장 금지.
        </p>
      </div>

      <div className="post-init__section">
        <div className="post-init__label">TOTP 시크릿 (수동 입력용)</div>
        <div className="post-init__code">
          <code>{result.totpSecret}</code>
          <CopyButton value={result.totpSecret} />
        </div>
        <p className="post-init__hint">
          복구 코드와 다른 위치에 별도 보관 권장. 다른 기기에 TOTP를 재등록할 때 사용합니다.
        </p>
      </div>

      <div className="post-init__section">
        <div className="post-init__label">QR 코드 (인증기 앱으로 스캔)</div>
        <img src={result.totpQrDataUrl} alt="TOTP QR" className="post-init__qr" />
      </div>

      {/* 체크박스 2개 — 둘 다 확인해야 진행 가능 */}
      <div className="post-init__confirm-stack">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={savedRecovery}
            onChange={(e) => setSavedRecovery(e.target.checked)}
          />
          <span>복구 코드를 종이에 기록하거나 안전한 오프라인 위치에 보관했습니다</span>
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={savedTotp}
            onChange={(e) => setSavedTotp(e.target.checked)}
          />
          <span>TOTP 인증기 앱에 등록하고 6자리 코드 생성을 확인했습니다</span>
        </label>
      </div>

      <button
        type="button"
        className="btn btn--primary"
        disabled={!allSaved}
        onClick={onConfirm}
        title={!allSaved ? '위 두 항목을 모두 확인해야 진행할 수 있습니다' : undefined}
      >
        로그인 화면으로
      </button>
    </div>
  );
}

function LoginForm({
  recoveryCodeOnScreen,
  onAckRecoveryCode,
  myIpIsTrusted,
  myIp,
}: {
  recoveryCodeOnScreen: string | null;
  onAckRecoveryCode: () => void;
  myIpIsTrusted: boolean;
  myIp: string | null;
}) {
  const [password, setPassword] = useState('');
  const [totpToken, setTotpToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const mut = useMutation({
    mutationFn: ({ pw, token }: { pw: string; token: string }) => authApi.login(pw, token),
    onSuccess: async (result: LoginResult) => {
      // v2 이관 결과 토스트 표시 (1회)
      if (result.v2Migration) {
        const m = result.v2Migration;
        if (m.migrated) {
          toast.success(
            `v2 데이터 이관 완료 — 아이템 ${m.itemsCount}개 (${m.durationMs}ms)`,
          );
        } else if (m.error) {
          toast.error(`v2 이관 실패: ${m.error}`);
        }
      }
      await qc.invalidateQueries({ queryKey: AUTH_STATUS_KEY });
      // 잠금 상태 갱신 후 RequireAuth가 자동 리다이렉트
    },
    onError: (err) => {
      setError(isApiError(err) ? mapLoginError(err.error) : '로그인 실패');
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mut.mutate({ pw: password, token: totpToken });
  };

  return (
    <>
      {recoveryCodeOnScreen && (
        <div className="callout callout--warn">
          <p><b>새 복구 코드</b> — 이전 코드는 무효화되었습니다. 안전한 곳에 저장하세요.</p>
          <div className="post-init__code">
            <code>{recoveryCodeOnScreen}</code>
            <CopyButton value={recoveryCodeOnScreen} />
          </div>
          <button type="button" className="btn btn--ghost" onClick={onAckRecoveryCode}>저장했습니다</button>
        </div>
      )}
      <form onSubmit={onSubmit} className="stack">
        <label>
          <span>마스터 패스워드</span>
          <input type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <label>
          <span>
            TOTP 6자리
            {myIpIsTrusted && <small className="muted"> (신뢰 IP — 입력 안 해도 됨)</small>}
          </span>
          <input inputMode="numeric" maxLength={8} value={totpToken}
            onChange={(e) => setTotpToken(e.target.value)}
            placeholder={myIpIsTrusted ? '신뢰 IP에서는 생략 가능' : ''} />
        </label>
        {myIpIsTrusted && (
          <div className="callout" style={{ borderLeft: '4px solid var(--ok)', padding: '8px 12px' }}>
            <small>
              ✓ 현재 IP <code>{myIp}</code>는 신뢰 IP로 등록되어 있습니다. TOTP 없이 로그인 가능합니다.
            </small>
          </div>
        )}
        {error && <div className="error-inline">{error}</div>}
        <button type="submit" className="btn btn--primary" disabled={mut.isPending}>
          {mut.isPending ? '확인 중…' : '잠금 해제'}
        </button>
      </form>
    </>
  );
}

function ResetForm({ onDone, onCancel }: { onDone: (code: string) => void; onCancel: () => void }) {
  const [code, setCode] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const toast = useToast();
  const mut = useMutation({
    mutationFn: ({ rc, pw }: { rc: string; pw: string }) => authApi.reset(rc, pw),
    onSuccess: (r) => onDone(r.newRecoveryCode),
    onError: (err) => toast.error(isApiError(err) ? mapLoginError(err.error) : '재설정 실패'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error('새 비밀번호는 8자 이상'); return; }
    if (newPw !== confirm) { toast.error('확인 비밀번호가 일치하지 않습니다'); return; }
    mut.mutate({ rc: code, pw: newPw });
  };

  return (
    <form onSubmit={onSubmit} className="stack">
      <p className="muted">복구 코드로 마스터 패스워드를 재설정합니다. 기존 데이터는 보존됩니다.</p>
      <label>
        <span>복구 코드</span>
        <input autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX" />
      </label>
      <label>
        <span>새 마스터 패스워드 (8자 이상)</span>
        <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
      </label>
      <label>
        <span>확인</span>
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </label>
      <div className="row">
        <button type="button" className="btn btn--ghost" onClick={onCancel}>취소</button>
        <button type="submit" className="btn btn--primary" disabled={mut.isPending}>
          {mut.isPending ? '처리 중…' : '재설정'}
        </button>
      </div>
    </form>
  );
}

function mapLoginError(code: string): string {
  switch (code) {
    case 'invalid_credentials': return '패스워드 또는 TOTP가 올바르지 않습니다';
    case 'invalid_recovery_code': return '복구 코드가 올바르지 않습니다';
    case 'invalid_request': return '입력값이 올바르지 않습니다';
    case 'not_initialized': return '아직 초기 설정이 완료되지 않았습니다';
    case 'already_initialized': return '이미 초기 설정이 완료됨';
    case 'forbidden': return 'IP가 차단되어 있습니다';
    default: return '요청 실패';
  }
}
